/**
 * Venttra - Side-by-side localization review layout
 * Compares master English content with DE, FR, IT translations for transcreation review.
 */
import React from "react";
import {
  EntityCustomViewParams,
  PropertyFieldBinding,
  useSnackbarController,
  type Property,
} from "@firecms/core";
import {
  Button,
  Markdown,
  Paper,
  Typography,
} from "@firecms/ui";
import { getApps } from "firebase/app";
import { doc, getFirestore, serverTimestamp, updateDoc } from "firebase/firestore";
import { CONTENT_LOCALES, type ContentLocale, LOCALE_LABELS } from "../constants";
import { CONTENT_STATUS } from "../lib/contentStatus";
import {
  applyPublishedSnapshot,
  hasPublishedSnapshot,
} from "../lib/publishedSnapshot";
import { DestinationLocalizationReview } from "./DestinationLocalizationReview";

const REVIEW_LOCALE_LABELS: Record<ContentLocale, string> = {
  ...LOCALE_LABELS,
  en: "English (Master)",
};

export type LocalizationReviewConfig = {
  /** Extract title for a locale from entity values */
  getTitle: (locale: ContentLocale, values: Record<string, unknown>) => string;
  /** Extract intro/content for a locale from entity values */
  getIntro: (locale: ContentLocale, values: Record<string, unknown>) => string;
  /** Whether intro is markdown */
  introIsMarkdown?: boolean;
};

const DESTINATION_CONFIG: LocalizationReviewConfig = {
  getTitle: (_, values) => (values.name as string) ?? "",
  getIntro: (locale, values) => {
    const content = values.content as Record<string, { intro?: string }> | undefined;
    return content?.[locale]?.intro ?? "";
  },
  introIsMarkdown: true,
};

const GUIDE_CONFIG: LocalizationReviewConfig = {
  getTitle: (locale, values) => {
    const title = values.title as Record<string, string> | undefined;
    return title?.[locale] ?? "";
  },
  getIntro: (locale, values) => {
    const desc = values.description as Record<string, string> | undefined;
    return desc?.[locale] ?? "";
  },
  introIsMarkdown: true,
};

const ARTICLE_CONFIG: LocalizationReviewConfig = {
  getTitle: (locale, values) => {
    const title = values.title as Record<string, string> | undefined;
    return title?.[locale] ?? "";
  },
  getIntro: (locale, values) => {
    const content = values.content as Record<string, string> | undefined;
    return content?.[locale] ?? "";
  },
  introIsMarkdown: true,
};

const AUTHOR_CONFIG: LocalizationReviewConfig = {
  getTitle: (_, values) => (values.name as string) ?? "",
  getIntro: (locale, values) => {
    const bio = values.bio as Record<string, string> | undefined;
    return bio?.[locale] ?? "";
  },
  introIsMarkdown: true,
};

function LocalizationReviewLayout({
  config,
  values,
  formContext,
  statusPropertyKey,
  statusProperty,
  collectionPath,
  entityId,
}: {
  config: LocalizationReviewConfig;
  values: Record<string, unknown>;
  formContext: EntityCustomViewParams<Record<string, unknown>>["formContext"];
  statusPropertyKey: string;
  statusProperty: Property;
  collectionPath?: string;
  entityId?: string;
}) {
  const snackbarController = useSnackbarController();
  const currentStatus = (values[statusPropertyKey] as string) ?? "draft";
  const canMarkReady =
    currentStatus === "draft" ||
    currentStatus === "unpublished" ||
    currentStatus === "archived";
  const canMarkLiveReady =
    currentStatus === "published_draft" || currentStatus === "published";
  const canRevert =
    hasPublishedSnapshot(values) &&
    (currentStatus === "published_draft" || currentStatus === "published_ready");

  const handleMarkReady = async () => {
    const previousStatus = values[statusPropertyKey];
    formContext.setFieldValue(statusPropertyKey, "ready_for_publishing");
    try {
      const saveValues = {
        ...values,
        [statusPropertyKey]: "ready_for_publishing",
      } as Record<string, unknown>;
      await formContext.save(saveValues);
      snackbarController.open({
        type: "success",
        message:
          "Marked Ready for Publishing. Use the Publish button in the top bar to rebuild the site.",
      });
    } catch (err) {
      formContext.setFieldValue(statusPropertyKey, previousStatus);
      snackbarController.open({
        type: "error",
        message: err instanceof Error ? err.message : "Could not mark Ready for Publishing",
      });
    }
  };

  const handleMarkLiveReady = async () => {
    const previousStatus = values[statusPropertyKey];
    formContext.setFieldValue(statusPropertyKey, "published_ready");
    try {
      await formContext.save({
        ...values,
        [statusPropertyKey]: "published_ready",
      } as Record<string, unknown>);
      snackbarController.open({
        type: "success",
        message: `Marked ${CONTENT_STATUS.published_ready}. Use Publish in the top bar to update the live page.`,
      });
    } catch (err) {
      formContext.setFieldValue(statusPropertyKey, previousStatus);
      snackbarController.open({
        type: "error",
        message: err instanceof Error ? err.message : "Could not mark Live · Ready",
      });
    }
  };

  const handleRevert = async () => {
    if (!collectionPath || !entityId || !hasPublishedSnapshot(values)) {
      snackbarController.open({ type: "error", message: "No published snapshot to revert to." });
      return;
    }
    const app = getApps()[0];
    if (!app) {
      snackbarController.open({ type: "error", message: "Firebase is not initialized." });
      return;
    }
    try {
      const restored = applyPublishedSnapshot(
        values,
        values.published_snapshot as Record<string, unknown>,
      );
      const { status: _s, updated_at: _u, created_at: _c, ...fields } = restored;
      await updateDoc(doc(getFirestore(app), collectionPath, entityId), {
        ...fields,
        status: "published",
        updated_at: serverTimestamp(),
      });
      snackbarController.open({
        type: "success",
        message: "Reverted to the live version. Reload the entity to see it.",
      });
    } catch (err) {
      snackbarController.open({
        type: "error",
        message: err instanceof Error ? err.message : "Revert failed",
      });
    }
  };

  return (
    <div className="flex flex-col gap-6 p-4">
      {/* Status control - prominent at top */}
      <Paper className="p-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-col gap-2 min-w-0">
          <Typography variant="label" className="font-medium">
            Document status
          </Typography>
          <div className="flex items-center gap-3 flex-wrap">
            <PropertyFieldBinding
              propertyKey={statusPropertyKey}
              property={statusProperty}
              context={formContext}
              minimalistView
            />
            {canMarkReady && (
              <Button
                variant="filled"
                color="primary"
                onClick={() => void handleMarkReady()}
                aria-label="Mark ready for publishing"
              >
                Mark Ready for Publishing
              </Button>
            )}
            {canMarkLiveReady && (
              <Button
                variant="filled"
                color="primary"
                onClick={() => void handleMarkLiveReady()}
                aria-label="Mark live ready"
              >
                Mark Live · Ready
              </Button>
            )}
            {canRevert && (
              <Button
                variant="outlined"
                onClick={() => void handleRevert()}
                aria-label="Revert to live version"
              >
                Revert to live
              </Button>
            )}
          </div>
          <Typography variant="caption" className="text-gray-600">
            Publishing and unpublishing the live site happen from the Publish button in the top bar
            — not when status is changed here.{" "}
            {CONTENT_STATUS.published_draft} / {CONTENT_STATUS.published_ready} mean the page is
            still live from the last Publish while CMS edits are pending.
          </Typography>
        </div>
      </Paper>

      {/* Side-by-side comparison - stacks vertically on viewports < 768px */}
      <div
        className="grid gap-4 overflow-x-auto localization-review-grid"
        style={{
          gridTemplateColumns: `repeat(${CONTENT_LOCALES.length}, minmax(240px, 1fr))`,
        }}
        role="region"
        aria-label="Localization comparison"
      >
        {CONTENT_LOCALES.map((locale) => {
          const title = config.getTitle(locale, values);
          const intro = config.getIntro(locale, values);
          const isEmpty = !title && !intro;

          return (
            <div
              key={locale}
              role="article"
              aria-labelledby={`locale-${locale}`}
              className="min-w-0"
            >
              <Paper className="p-4 flex flex-col min-h-[200px] overflow-hidden h-full">
                <Typography
                  variant="subtitle2"
                  className="font-semibold mb-2 text-primary"
                  component="h3"
                  id={`locale-${locale}`}
                >
                  {REVIEW_LOCALE_LABELS[locale]}
                </Typography>
              {isEmpty ? (
                <Typography variant="body2" className="text-gray-500 italic">
                  No content
                </Typography>
              ) : (
                <>
                  {title && (
                    <Typography variant="body1" className="font-medium mb-2">
                      {title}
                    </Typography>
                  )}
                  {intro && (
                    <div className="flex-1 overflow-y-auto text-sm">
                      {config.introIsMarkdown ? (
                        <Markdown source={intro} size="small" />
                      ) : (
                        <Typography variant="body2">{intro}</Typography>
                      )}
                    </div>
                  )}
                </>
              )}
              </Paper>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Generic factory for creating localization review views */
function createLocalizationReviewView(
  config: LocalizationReviewConfig,
  builderName: string
) {
  const Builder = function (
    props: EntityCustomViewParams<Record<string, unknown>>
  ) {
    const { collection, modifiedValues, formContext, entity } = props;
    const values = modifiedValues ?? props.entity?.values ?? {};
    const statusProp = collection.properties?.status;
    if (!statusProp || typeof statusProp === "function") return null;
    const resolved = statusProp as Property;
    return (
      <LocalizationReviewLayout
        config={config}
        values={values}
        formContext={formContext}
        statusPropertyKey="status"
        statusProperty={resolved}
        collectionPath={collection.path ?? entity?.path}
        entityId={entity?.id}
      />
    );
  };
  Object.defineProperty(Builder, "name", { value: builderName, configurable: true });

  return {
    key: "localization-review",
    name: "Localization Review",
    includeActions: true as const,
    position: "start" as const,
    Builder,
  };
}

/** Factory for destinations collection */
export function createDestinationsLocalizationReviewView() {
  const Builder = function (
    props: EntityCustomViewParams<Record<string, unknown>>
  ) {
    const { collection, modifiedValues, formContext, entity } = props;
    // Merge entity + form values so internal research_* fields still render even if
    // the form state briefly omits them (they drive the Editorial research panel).
    const values = {
      ...(entity?.values ?? {}),
      ...(modifiedValues ?? {}),
    } as Record<string, unknown>;
    const statusProp = collection.properties?.status;
    if (!statusProp || typeof statusProp === "function") return null;
    const resolved = statusProp as Property;
    return (
      <DestinationLocalizationReview
        values={values}
        formContext={formContext}
        statusPropertyKey="status"
        statusProperty={resolved}
        entityId={entity?.id}
      />
    );
  };
  Object.defineProperty(Builder, "name", {
    value: "DestinationsLocalizationReviewBuilder",
    configurable: true,
  });

  return {
    key: "localization-review",
    name: "Localization Review",
    includeActions: true as const,
    position: "start" as const,
    Builder,
  };
}

/** Factory for guides collection */
export function createGuidesLocalizationReviewView() {
  return createLocalizationReviewView(GUIDE_CONFIG, "GuidesLocalizationReviewBuilder");
}

/** Factory for articles collection */
export function createArticlesLocalizationReviewView() {
  return createLocalizationReviewView(ARTICLE_CONFIG, "ArticlesLocalizationReviewBuilder");
}

/** Factory for authors collection */
export function createAuthorsLocalizationReviewView() {
  return createLocalizationReviewView(AUTHOR_CONFIG, "AuthorsLocalizationReviewBuilder");
}
